import axios from 'axios';

/**
 * Task Service
 * Handles all API calls related to tasks
 */
export const taskService = {
  // ============================================
  // TASK QUERIES
  // ============================================

  /**
   * Get tasks with filters
   * @param {Object} params - {
   *   deal_id?, contact_id?, assigned_to?, status?, priority?,
   *   due_date_from?, due_date_to?, search?, page?, limit?,
   *   task_type?  // 'callback' | 'follow_up' | 'general' | string | string[]
   * }
   * @returns {Promise<Object>} { tasks: Task[], pagination: Pagination }
   */
  async getTasks(params = {}) {
    try {
      const queryParams = { ...params };
      if (Array.isArray(queryParams.task_type)) {
        queryParams.task_type = queryParams.task_type.filter(Boolean).join(',');
      }
      const response = await axios.get('/tasks', { params: queryParams });
      return response.data;
    } catch (error) {
      console.error('Error fetching tasks:', error);
      throw error;
    }
  },

  /**
   * Convenience wrapper for the "callback list" view.
   * Defaults to pending callbacks; extra params are merged in.
   * @param {Object} params - { status?, page?, limit?, assigned_to?, ... }
   * @returns {Promise<Object>} { tasks: Task[], pagination: Pagination }
   */
  async getCallbacks(params = {}) {
    return this.getTasks({
      task_type: 'callback',
      status: 'pending',
      ...params,
    });
  },

  /**
   * Get my tasks (assigned to current user)
   * @param {Object} params - { include_completed? }
   * @returns {Promise<Object>} { tasks: Task[] }
   */
  async getMyTasks(params = {}) {
    try {
      const response = await axios.get('/tasks/my', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching my tasks:', error);
      throw error;
    }
  },

  /**
   * Get tasks due today
   * @returns {Promise<Object>} { tasks: Task[] }
   */
  async getTodayTasks() {
    try {
      const response = await axios.get('/tasks/today');
      return response.data;
    } catch (error) {
      console.error('Error fetching today tasks:', error);
      throw error;
    }
  },

  /**
   * Get overdue tasks
   * @returns {Promise<Object>} { tasks: Task[] }
   */
  async getOverdueTasks() {
    try {
      const response = await axios.get('/tasks/overdue');
      return response.data;
    } catch (error) {
      console.error('Error fetching overdue tasks:', error);
      throw error;
    }
  },

  /**
   * Get upcoming tasks
   * @param {Object} params - { days?: number }
   * @returns {Promise<Object>} { tasks: Task[] }
   */
  async getUpcomingTasks(params = {}) {
    try {
      const response = await axios.get('/tasks/upcoming', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching upcoming tasks:', error);
      throw error;
    }
  },

  /**
   * Get task statistics
   * @returns {Promise<Object>} { stats: TaskStats }
   */
  async getStats() {
    try {
      const response = await axios.get('/tasks/stats');
      return response.data;
    } catch (error) {
      console.error('Error fetching task stats:', error);
      throw error;
    }
  },

  /**
   * Get a single task by ID
   * @param {string} taskId
   * @returns {Promise<Task>}
   */
  async getTaskById(taskId) {
    try {
      const response = await axios.get(`/tasks/${taskId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching task:', error);
      throw error;
    }
  },

  // ============================================
  // TASK MUTATIONS
  // ============================================

  /**
   * Create a new task
   * @param {Object} taskData - { title, description?, priority?, due_date, due_time?, deal_id?, contact_id?, assigned_to?, reminder_at? }
   * @returns {Promise<Task>}
   */
  async createTask(taskData) {
    try {
      const response = await axios.post('/tasks', taskData);
      return response.data?.task || response.data;
    } catch (error) {
      console.error('Error creating task:', error);
      throw error;
    }
  },

  /**
   * Update a task
   * @param {string} taskId
   * @param {Object} taskData
   * @returns {Promise<Task>}
   */
  async updateTask(taskId, taskData) {
    try {
      const response = await axios.patch(`/tasks/${taskId}`, taskData);
      return response.data;
    } catch (error) {
      console.error('Error updating task:', error);
      throw error;
    }
  },

  /**
   * Delete a task
   * @param {string} taskId
   * @returns {Promise<void>}
   */
  async deleteTask(taskId) {
    try {
      await axios.delete(`/tasks/${taskId}`);
    } catch (error) {
      console.error('Error deleting task:', error);
      throw error;
    }
  },

  // ============================================
  // TASK STATUS
  // ============================================

  /**
   * Complete a task
   * @param {string} taskId
   * @returns {Promise<Task>}
   */
  async completeTask(taskId) {
    try {
      const response = await axios.post(`/tasks/${taskId}/complete`);
      return response.data;
    } catch (error) {
      console.error('Error completing task:', error);
      throw error;
    }
  },

  /**
   * Reopen a completed task
   * @param {string} taskId
   * @returns {Promise<Task>}
   */
  async reopenTask(taskId) {
    try {
      const response = await axios.post(`/tasks/${taskId}/reopen`);
      return response.data;
    } catch (error) {
      console.error('Error reopening task:', error);
      throw error;
    }
  },

  /**
   * Update task status
   * @param {string} taskId
   * @param {string} status - 'pending' | 'in_progress' | 'completed' | 'cancelled'
   * @returns {Promise<Task>}
   */
  async updateStatus(taskId, status) {
    try {
      const response = await axios.patch(`/tasks/${taskId}/status`, { status });
      return response.data;
    } catch (error) {
      console.error('Error updating task status:', error);
      throw error;
    }
  },

  // ============================================
  // BULK OPERATIONS
  // ============================================

  /**
   * Bulk complete tasks
   * @param {string[]} taskIds
   * @returns {Promise<Object>}
   */
  async bulkComplete(taskIds) {
    try {
      const response = await axios.post('/tasks/bulk/complete', { task_ids: taskIds });
      return response.data;
    } catch (error) {
      console.error('Error bulk completing tasks:', error);
      throw error;
    }
  },

  /**
   * Bulk assign tasks
   * @param {string[]} taskIds
   * @param {string} assignedTo
   * @returns {Promise<Object>}
   */
  async bulkAssign(taskIds, assignedTo) {
    try {
      const response = await axios.post('/tasks/bulk/assign', {
        task_ids: taskIds,
        assigned_to: assignedTo,
      });
      return response.data;
    } catch (error) {
      console.error('Error bulk assigning tasks:', error);
      throw error;
    }
  },

  /**
   * Bulk delete tasks
   * @param {string[]} taskIds
   * @returns {Promise<Object>}
   */
  async bulkDelete(taskIds) {
    try {
      const response = await axios.post('/tasks/bulk/delete', { task_ids: taskIds });
      return response.data;
    } catch (error) {
      console.error('Error bulk deleting tasks:', error);
      throw error;
    }
  },

  // ============================================
  // DEAL TASKS
  // ============================================

  /**
   * Get tasks for a specific deal
   * @param {string} dealId
   * @returns {Promise<Object>} { tasks: Task[] }
   */
  async getDealTasks(dealId) {
    try {
      const response = await axios.get(`/deals/${dealId}/tasks`);
      return response.data;
    } catch (error) {
      console.error('Error fetching deal tasks:', error);
      throw error;
    }
  },

  /**
   * Create a task for a deal
   * @param {string} dealId
   * @param {Object} taskData - { title, description?, priority?, due_date, due_time?, assigned_to?, reminder_at? }
   * @returns {Promise<Task>}
   */
  async createDealTask(dealId, taskData) {
    try {
      const response = await axios.post(`/deals/${dealId}/tasks`, taskData);
      return response.data?.task || response.data;
    } catch (error) {
      console.error('Error creating deal task:', error);
      throw error;
    }
  },

  /**
   * Count open project-home tasks for a deal
   */
  async getDealProjectOpenTaskCount(dealId) {
    try {
      const response = await axios.get(`/deals/${dealId}/tasks/project-open-count`);
      return {
        count: response.data?.count ?? 0,
        project_id: response.data?.project_id ?? null,
      };
    } catch (error) {
      console.error('Error fetching project open task count:', error);
      throw error;
    }
  },

  /**
   * Get tasks for a project (project home)
   */
  async getProjectTasks(projectId, params = {}) {
    try {
      const response = await axios.get(`/projects/${projectId}/tasks`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching project tasks:', error);
      throw error;
    }
  },

  /**
   * Create a project-home task
   */
  async createProjectTask(projectId, taskData) {
    try {
      const response = await axios.post(`/projects/${projectId}/tasks`, taskData);
      return response.data?.task || response.data;
    } catch (error) {
      console.error('Error creating project task:', error);
      throw error;
    }
  },
};

export default taskService;
